import { forwardRef, useContext } from "react";
import { ImageSourcePropType, View as RNView } from "react-native";
import { useTheme } from "tamagui";

import TestimonialLittleFaces from "@/assets/images/landing_page/v3/testimonial-little-faces.png";
import {
  Image,
  Text,
  View,
  XStack,
  YStack,
  useImageAspectRatio,
} from "@/components";
import AnimatedStrokeBorder from "@/components/animated/AnimatedStrokeBorder";
import { LayoutContext } from "@/components/LayoutProvider";
import SectionHeader from "./SectionHeader";

const ShimmerTag = () => {
  const theme = useTheme();
  return (
    <AnimatedStrokeBorder
      marginTop="$100"
      width="min-content"
      radius="$200"
      baseColor={theme.fillPrompt?.get()}
      segmentColor={theme.strokePrompt?.get()}
    >
      <XStack
        backgroundColor="$fillPrompt"
        borderRadius="$200"
        overflow="hidden"
        padding="$100"
        width="min-content"
        gap="$100"
      >
        <Image
          source={TestimonialLittleFaces}
          height={16}
          width={35}
          resizeMode="contain"
        />
        <Text size="$sm" medium color="$textPrompt" whiteSpace="nowrap">
          Used by over 11,000 people
        </Text>
      </XStack>
    </AnimatedStrokeBorder>
  );
};

export type TestimonialProps = {
  headerTextInPrimaryColor: string;
  headerTextInBrandColor: string;
  image: ImageSourcePropType;
  includeShimmerTag?: boolean;
  quote: string;
  name: string;
  credential?: string;
};

const Testimonial = forwardRef<RNView, TestimonialProps>(
  (
    {
      includeShimmerTag,
      quote,
      name,
      credential,
      image,
      headerTextInPrimaryColor,
      headerTextInBrandColor,
    },
    ref,
  ) => {
    const { lg } = useContext(LayoutContext);
    const { aspectRatio: imageAspectRatio, onLoad: onImageLoad } =
      useImageAspectRatio();

    if (lg) {
      return (
        <XStack
          maxWidth="$landingPageConstraint"
          alignSelf="center"
          alignItems="center"
          paddingHorizontal="$250"
          gap="$800"
        >
          {includeShimmerTag && (
            <Image
              flex={1}
              maxHeight={445}
              width="auto"
              aspectRatio={imageAspectRatio}
              source={image}
              borderRadius="$200"
              onLoad={onImageLoad}
            />
          )}
          <View width="40%">
            <SectionHeader
              textInPrimaryColor={headerTextInPrimaryColor}
              textInBrandColor={headerTextInBrandColor}
              ref={ref}
            />
            {includeShimmerTag && <ShimmerTag />}
            <Text marginVertical="$400" $lg={{ size: "$lg" }}>
              &ldquo;{quote}&rdquo;
            </Text>
            <Text semibold $lg={{ size: "$lg" }}>
              {name}
            </Text>
            {credential && <Text marginTop="$100">{credential}</Text>}
          </View>
          {!includeShimmerTag && (
            <Image
              flex={1}
              maxHeight={445}
              aspectRatio={imageAspectRatio}
              source={image}
              borderRadius="$200"
              onLoad={onImageLoad}
            />
          )}
        </XStack>
      );
    }

    return (
      <YStack
        maxWidth="$landingPageConstraint"
        alignSelf="center"
        paddingHorizontal="$250"
      >
        <SectionHeader
          textInPrimaryColor={headerTextInPrimaryColor}
          textInBrandColor={headerTextInBrandColor}
          ref={ref}
        />
        {includeShimmerTag && <ShimmerTag />}
        <Image
          alignSelf="center"
          width={275}
          height={205}
          source={image}
          borderRadius="$200"
          marginTop="$500"
          marginBottom="$400"
        />
        <Text fontSize={18} marginBottom="$400" $lg={{ size: "$lg" }}>
          &ldquo;{quote}&rdquo;
        </Text>
        <Text semibold $lg={{ size: "$lg" }}>
          {name}
        </Text>
        {credential && <Text marginTop="$100">{credential}</Text>}
      </YStack>
    );
  },
);
export default Testimonial;
